import React from "react";
import { PropertyPanelSection } from "@showitinc/showit-wc";
import AppConstants from "../../../constants/AppConstants";
import ElementPropertiesHorizontalLocking from "./ElementPropertiesHorizontalLocking";
import ElementPropertiesVerticalLocking from "./ElementPropertiesVerticalLocking";
import SelectionStore from "../../../stores/SelectionStore";
import IDUtils from "../../../utils/IDUtils";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import styles from "./ElementPropertiesLocking.module.css";
import { GROUP } from "@/constants/DataTypes";
import {
  WINDOW_HEIGHT,
  GROW_WITH_CONTENT,
  SCROLLING_VIEWS,
  SPACING_WINDOW,
} from "../../../constants/CanvasTypeConstants.mjs";

const { LAYOUT_DESKTOP } = AppConstants;

const VERTICAL_LOCK_CONTAINER_TYPES = [WINDOW_HEIGHT, GROW_WITH_CONTENT, SCROLLING_VIEWS];

const VERTICAL_DIRECTIONS = ["up", "down"];

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS[number]} Layout
 */

/**
 * Which lock controls the selection gets. In a condensed strip the neighbouring views
 * sit where the window edges would be on the strip's axis, so a selection that includes
 * a view element loses that axis's lock; the cross axis still reaches the window.
 * Unknown directions count as horizontal, as the renderer narrows them to left.
 * @param {Layout} layout
 * @param {import("immutable").Map<string, *> | undefined} blockLayout - the block's data for that layout
 * @param {string[]} elementIds - the selected elements' ids, each a path from the block
 * @returns {{ supportsHorizontalLocking: boolean, supportsVerticalLocking: boolean }}
 */
export const getLockSupport = (layout, blockLayout, elementIds) => {
  const nature = blockLayout?.get("nature");
  const inCondensedView =
    nature === SCROLLING_VIEWS &&
    blockLayout.get("spacing") !== SPACING_WINDOW &&
    elementIds.some((elementId) => IDUtils.isStateElementPath(IDUtils.getArray(elementId)));
  const inVerticalStrip =
    inCondensedView && VERTICAL_DIRECTIONS.includes(blockLayout.get("direction"));
  const inHorizontalStrip = inCondensedView && !inVerticalStrip;

  return {
    supportsHorizontalLocking: !inHorizontalStrip && layout === LAYOUT_DESKTOP,
    supportsVerticalLocking: !inVerticalStrip && VERTICAL_LOCK_CONTAINER_TYPES.includes(nature),
  };
};

/**
 * @component ElementPropertiesPositionPanel
 * @description A React component that renders a panel for configuring position properties
 * of selected elements. It includes options for centering elements on the canvas and
 * locking their horizontal and vertical positions, depending on the current layout and block nature.
 *
 * @param {Object} props - The component props
 * @param {string} props.title - optional title override for the panel (defaults to "Position")
 * @param {boolean} props.disabled - shows the locking controls without letting them change
 */
const ElementPropertiesPositionPanel = ({ title, disabled }) => {
  const fullElements = SelectionStore.getFullElements();
  const allSelectedElements = Array.from(fullElements.values());
  const layout = SelectionStore.getLayout();
  const block = SelectionStore.getBlock();

  const { supportsHorizontalLocking, supportsVerticalLocking } = getLockSupport(
    layout,
    block.get(layout),
    allSelectedElements.map((element) => element.getIn(["selected", "id"])),
  );

  const isAnyElementLocked = allSelectedElements.some((element) => element.lock);

  const getOverallLockValue = (lockProp) => {
    const lockValues = allSelectedElements.map((element) => element.getIn([layout, lockProp], ""));
    const uniqueValues = new Set(lockValues);
    return uniqueValues.size === 1 ? lockValues[0] : "";
  };

  const allowStretch = !allSelectedElements.some((element) => element.get("type") === GROUP);

  const horizLockVal = getOverallLockValue("lockH");
  const vertLockVal = getOverallLockValue("lockV");

  const _clickLocking = (prop, val) => {
    if (!disabled) {
      const propInfo = { name: `${layout}.${prop}` };
      const targetElements = SelectionStore.getFullElements();
      const blockId = targetElements[0].getIn(["selected", "blockId"]);

      const currentOverallValue = getOverallLockValue(prop);
      // Now the value should toggle on or off if they were to try and batch turn off edge locking
      const newValue = currentOverallValue === val ? "" : val;

      const updates = targetElements.map((element) => {
        const elemId = element.getIn(["selected", "id"]);
        const [, ...newIDArray] = IDUtils.getArray(elemId);
        const newPropertyArray = IDUtils.getArray(propInfo.name);

        return {
          key: [...newIDArray, ...newPropertyArray],
          value: newValue,
          addUndo: false,
        };
      });

      const updatesObject = {
        id: blockId,
        updates,
      };
      DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: updatesObject });
    }
  };

  const checked = (lockValue) => (curr, prop) =>
    lockValue === curr && curr === prop ? styles.active : "";

  return (
    <>
      {supportsHorizontalLocking && (
        <ElementPropertiesHorizontalLocking
          isLocked={isAnyElementLocked}
          clickLocking={_clickLocking}
          checked={checked(horizLockVal)}
          elemLockH={horizLockVal}
          allowStretch={allowStretch}
          disabled={disabled}
        />
      )}

      {supportsVerticalLocking && (
        <ElementPropertiesVerticalLocking
          isLocked={isAnyElementLocked}
          clickLocking={_clickLocking}
          checked={checked(vertLockVal)}
          elemLockV={vertLockVal}
          allowStretch={allowStretch}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default ElementPropertiesPositionPanel;
